import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Paperclip, XCircle } from 'lucide-react';
import {
  ADMISSION_PRIORITIES,
  NPHIES_SERVICE_EVENT_TYPES,
  type AdmissionPriority,
  type NphiesAdmissionDetailsDto,
  type NphiesAdmissionReadinessDto,
  type NphiesInpatientReadinessDto,
  type NphiesReadinessDto,
  type NphiesReadinessItem,
  type NphiesReadinessLink,
  type NphiesServiceEventType,
} from '@his/shared';
import { get, put, uploadWithProgress } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { cn, money } from '@/lib/utils';
import { MoneyInput } from '@/components/MoneyInput';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, SkeletonText, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import type { SendCheckControls } from '../integrations/ExternalSendButton';
import { patientTabPath } from '../patients/patient-tabs';

const MONEY_PARAMS = new Set(['amount', 'requested', 'estimate']);
const LABELLED_PARAMS: Readonly<Record<string, string>> = { priority: 'nr_priority_', source: 'nr_source_', type: 'nr_service_event_', vital: 'nr_vital_name_' };
const IPD_LABELLED_PARAMS: Readonly<Record<string, string>> = { stay: 'intended_stay_', disposition: 'discharge_disposition_' };
const READINESS_KEY = 'nphies-readiness';
const ADMISSION_READINESS_KEY = 'nphies-admission-readiness';

export function readinessFixPath(link: NphiesReadinessLink): string | null {
  const id = link.id ? encodeURIComponent(link.id) : null;
  switch (link.target) {
    case 'admission':
      return id ? `/ipd/admissions/${id}?tab=${encodeURIComponent(link.section)}` : null;
    case 'patient':
      return id && link.id ? (link.section === 'insurance' ? patientTabPath(link.id, 'insurance') : `/patients/${id}/edit`) : null;
    case 'pre_authorization':
      return id ? `/insurance/pre-auths/${id}/edit` : null;
    case 'user':
      return id ? `/people/u:${id}/edit` : null;
    case 'specialty':
      return id ? `/admin/specialties/${id}/edit` : null;
    case 'bed':
      return id ? `/admin/beds/${id}/edit` : null;
    case 'branch':
      return id ? `/admin/branches/${id}/edit` : null;
    case 'encounter':
      return id ? `/consultations/${id}` : null;
    case 'lab_catalogue':
      return '/lab/catalogue';
    case 'code_mapping':
    case 'integration_settings':
      return '/admin/settings?tab=integrations';
    default:
      return null;
  }
}

export function readinessText(item: NphiesReadinessItem): string {
  const params: Record<string, string | number> = {};
  for (const [name, value] of Object.entries(item.params)) {
    const labelPrefix = LABELLED_PARAMS[name];
    if (MONEY_PARAMS.has(name) && typeof value === 'number') params[name] = money(value);
    else if (labelPrefix) params[name] = tr('billing', `${labelPrefix}${value}`);
    else if (IPD_LABELLED_PARAMS[name]) params[name] = tr('ipd', `${IPD_LABELLED_PARAMS[name]}${value}`);
    else params[name] = value;
  }
  return tr('billing', item.messageKey, params);
}

export function readinessGroups(items: readonly NphiesReadinessItem[]) {
  return {
    blocking: items.filter((item) => !item.ok && item.severity === 'block'),
    warnings: items.filter((item) => !item.ok && item.severity === 'warn'),
    done: items.filter((item) => item.ok),
  };
}

export function usePreAuthReadiness(preAuthId: string) {
  return useQuery({
    queryKey: [READINESS_KEY, preAuthId],
    queryFn: () => get<NphiesReadinessDto>(`/integrations/nphies/pre-auths/${preAuthId}/readiness`),
    refetchOnWindowFocus: false,
  });
}

function useRefreshReadiness(preAuthId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: [READINESS_KEY, preAuthId] });
    void queryClient.invalidateQueries({ queryKey: [ADMISSION_READINESS_KEY] });
  };
}

function ReadinessRow({ item, action }: { item: NphiesReadinessItem; action?: ReactNode }) {
  const Icon = item.ok ? CheckCircle2 : item.severity === 'block' ? XCircle : AlertTriangle;
  const tone = item.ok ? 'text-success' : item.severity === 'block' ? 'text-destructive' : 'text-warning';
  const path = !item.ok && item.link ? readinessFixPath(item.link) : null;
  return (
    <li className="flex flex-wrap items-start gap-x-2 gap-y-1 py-1.5 text-sm">
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', tone)} aria-hidden />
      <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{readinessText(item)}</span>
      {action}
      {path && item.link && (
        <Link to={path} className="focus-ring shrink-0 rounded text-xs font-medium text-primary hover:underline">
          {tr('billing', `nr_open_${item.link.target}`)}
        </Link>
      )}
    </li>
  );
}

function AttachFile({ preAuthId, onAttached }: { preAuthId: string; onAttached: () => void }) {
  const toast = useToast();
  const attach = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return uploadWithProgress<unknown>(`/integrations/nphies/pre-auths/${preAuthId}/attachments`, form, () => undefined);
    },
    onSuccess: () => {
      toast.success(tr('billing', 'nr_attached'));
      onAttached();
    },
    onError: (error) => toast.error(tr('billing', 'nr_could_not_attach'), describeError(error)),
  });
  return (
    <label className={cn('focus-ring inline-flex shrink-0 cursor-pointer items-center gap-1 rounded text-xs font-medium text-primary hover:underline', attach.isPending && 'pointer-events-none opacity-60')}>
      <Paperclip className="h-3.5 w-3.5" aria-hidden />
      {tr('billing', 'nr_attach')}
      <input
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        className="sr-only"
        disabled={attach.isPending}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) attach.mutate(file);
        }}
      />
    </label>
  );
}

function InpatientDetails({ preAuthId, details, onSaved }: { preAuthId: string; details: NphiesInpatientReadinessDto; onSaved: () => void }) {
  const toast = useToast();
  const { can } = useAuth();
  const mayEditAdmission = can('preauth.manage', 'nphies.preauth.send', 'admission.manage');
  const mayEditRequest = can('preauth.manage', 'nphies.preauth.send');
  const savedStay = details.estimatedStayDays === null ? '' : String(details.estimatedStayDays);
  const [stay, setStay] = useState(savedStay);
  const [estimate, setEstimate] = useState<number | null>(details.estimatedCostMinor);

  const saveAdmission = useMutation({
    mutationFn: (body: { admissionPriority?: AdmissionPriority; serviceEventType?: NphiesServiceEventType; estimatedCostMinor?: number | null }) =>
      put<NphiesAdmissionDetailsDto>(`/integrations/nphies/admission-details/${details.admissionId}`, body),
    onSuccess: () => {
      toast.success(tr('billing', 'nr_saved'));
      onSaved();
    },
    onError: (error) => toast.error(tr('billing', 'nr_could_not_save'), describeError(error)),
  });
  const saveStay = useMutation({
    mutationFn: () => put<NphiesReadinessDto>(`/integrations/nphies/pre-auths/${preAuthId}/estimated-stay`, { days: stay.trim() === '' ? null : Number(stay) }),
    onSuccess: () => {
      toast.success(tr('billing', 'nr_saved'));
      onSaved();
    },
    onError: (error) => toast.error(tr('billing', 'nr_could_not_save'), describeError(error)),
  });

  return (
    <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
      <h3 className="text-sm font-medium">{tr('billing', 'nr_details_title')}</h3>
      <Field label={tr('billing', 'nr_priority')}>
        <ChoiceButtons
          label={tr('billing', 'nr_priority')}
          value={details.admissionPriority}
          disabled={!mayEditAdmission || saveAdmission.isPending}
          onChange={(value) => saveAdmission.mutate({ admissionPriority: value })}
          options={ADMISSION_PRIORITIES.map((value) => ({ value, label: tr('billing', `nr_priority_${value}`) }))}
        />
      </Field>
      <Field label={tr('billing', 'nr_service_event')} hint={tr('billing', 'nr_service_event_hint')}>
        <ChoiceButtons
          label={tr('billing', 'nr_service_event')}
          value={details.serviceEventType}
          disabled={!mayEditAdmission || saveAdmission.isPending}
          onChange={(value) => saveAdmission.mutate({ serviceEventType: value })}
          options={NPHIES_SERVICE_EVENT_TYPES.map((value) => ({ value, label: tr('billing', `nr_service_event_${value}`) }))}
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={tr('billing', 'nr_estimated_stay')}>
          <div className="flex gap-2">
            <Input
              type="text"
              inputMode="numeric"
              dir="ltr"
              className="tabular"
              value={stay}
              disabled={!mayEditRequest}
              onChange={(event) => setStay(event.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
            />
            <Button type="button" size="sm" variant="outline" loading={saveStay.isPending} disabled={!mayEditRequest || stay === savedStay} onClick={() => saveStay.mutate()}>
              {tr('billing', 'nr_save')}
            </Button>
          </div>
        </Field>
        <Field label={tr('billing', 'nr_admission_estimate')}>
          <div className="flex gap-2">
            <MoneyInput valueMinor={estimate} onChangeMinor={setEstimate} disabled={!mayEditAdmission} />
            <Button
              type="button"
              size="sm"
              variant="outline"
              loading={saveAdmission.isPending && saveAdmission.variables?.estimatedCostMinor !== undefined}
              disabled={!mayEditAdmission || estimate === details.estimatedCostMinor}
              onClick={() => saveAdmission.mutate({ estimatedCostMinor: estimate })}
            >
              {tr('billing', 'nr_save')}
            </Button>
          </div>
        </Field>
      </div>
    </section>
  );
}

function ItemList({ items, preAuthId, mayAttach, onChanged }: { items: NphiesReadinessItem[]; preAuthId: string; mayAttach: boolean; onChanged: () => void }) {
  return (
    <ul className="divide-y divide-border">
      {items.map((item) => (
        <ReadinessRow key={item.key} item={item} action={item.key === 'attachments' && mayAttach ? <AttachFile preAuthId={preAuthId} onAttached={onChanged} /> : undefined} />
      ))}
    </ul>
  );
}

export function PreAuthReadinessContent({ preAuthId, readiness, onChanged }: { preAuthId: string; readiness: NphiesReadinessDto; onChanged: () => void }) {
  const { can } = useAuth();
  const [showDone, setShowDone] = useState(false);
  const { blocking, warnings, done } = readinessGroups(readiness.items);
  const mayAttach = can('preauth.manage', 'nphies.preauth.send');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <StatusBadge status={readiness.ready ? 'ready' : 'not_ready'} tone={readiness.ready ? 'success' : 'destructive'} label={tr('billing', readiness.ready ? 'nr_ready' : 'nr_not_ready')} />
        {blocking.length > 0 && <span className="text-destructive">{tr('billing', 'nr_blocking_count', { count: blocking.length })}</span>}
        {warnings.length > 0 && <span className="text-warning">{tr('billing', 'nr_warning_count', { count: warnings.length })}</span>}
      </div>
      {readiness.inpatient && <InpatientDetails key={readiness.inpatient.admissionId} preAuthId={preAuthId} details={readiness.inpatient} onSaved={onChanged} />}
      {blocking.length > 0 && <ItemList items={blocking} preAuthId={preAuthId} mayAttach={mayAttach} onChanged={onChanged} />}
      {warnings.length > 0 && <ItemList items={warnings} preAuthId={preAuthId} mayAttach={mayAttach} onChanged={onChanged} />}
      {done.length > 0 && (
        <div>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowDone(!showDone)} aria-expanded={showDone}>
            {showDone ? tr('billing', 'nr_hide_in_place') : tr('billing', 'nr_show_in_place')} · {tr('billing', 'nr_in_place_count', { count: done.length })}
          </Button>
          {showDone && <ItemList items={done} preAuthId={preAuthId} mayAttach={false} onChanged={onChanged} />}
        </div>
      )}
    </div>
  );
}

export function PreAuthReadiness({ preAuthId }: { preAuthId: string }) {
  const readiness = usePreAuthReadiness(preAuthId);
  const refresh = useRefreshReadiness(preAuthId);
  if (readiness.isLoading) return <SkeletonText lines={4} />;
  if (readiness.error) return <Alert variant="destructive">{describeError(readiness.error)}</Alert>;
  if (!readiness.data) return null;
  return <PreAuthReadinessContent preAuthId={preAuthId} readiness={readiness.data} onChanged={refresh} />;
}

export function AdmissionPreAuthReadiness({ admissionId }: { admissionId: string }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [ADMISSION_READINESS_KEY, admissionId],
    queryFn: () => get<NphiesAdmissionReadinessDto>(`/integrations/nphies/admission-readiness/${admissionId}`),
    refetchOnWindowFocus: false,
  });
  if (query.isLoading) return <SkeletonText lines={4} />;
  if (query.error) return <Alert variant="destructive">{describeError(query.error)}</Alert>;
  const data = query.data;
  if (!data?.preAuthId || !data.readiness) return <p className="text-sm text-muted-foreground">{tr('billing', 'nr_no_draft')}</p>;
  const preAuthId = data.preAuthId;
  return (
    <PreAuthReadinessContent
      preAuthId={preAuthId}
      readiness={data.readiness}
      onChanged={() => {
        void queryClient.invalidateQueries({ queryKey: [ADMISSION_READINESS_KEY, admissionId] });
        void queryClient.invalidateQueries({ queryKey: [READINESS_KEY, preAuthId] });
      }}
    />
  );
}

export function PreAuthSendCheck({ preAuthId, send, close, sending }: { preAuthId: string } & SendCheckControls) {
  const readiness = usePreAuthReadiness(preAuthId);
  const refresh = useRefreshReadiness(preAuthId);
  const ready = readiness.data?.ready ?? false;
  return (
    <Dialog
      open
      wide
      icon={ClipboardCheck}
      onClose={close}
      title={tr('billing', 'nr_title')}
      description={tr('billing', 'nr_intro')}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {tr('billing', 'nr_close')}
          </Button>
          <Button
            loading={sending}
            disabled={!ready || readiness.isFetching}
            onClick={() =>
              void send().then((sent) => {
                if (!sent) refresh();
              })
            }
          >
            {tr('billing', 'nr_send')}
          </Button>
        </>
      }
    >
      {readiness.isLoading && <SkeletonText lines={4} />}
      {readiness.error && <Alert variant="destructive">{describeError(readiness.error)}</Alert>}
      {readiness.data && <PreAuthReadinessContent preAuthId={preAuthId} readiness={readiness.data} onChanged={refresh} />}
      {readiness.data && !ready && <p className="mt-3 text-xs text-destructive">{tr('billing', 'nr_fix_first')}</p>}
    </Dialog>
  );
}
